/**
 * Planner — the multi-track plan-vs-actual timeline screen (§13/§13a/§17/§19).
 *
 * A level switcher walks the planning hierarchy (Corporate → Regional → Local →
 * Fleet → Pad → Well → Section). Fleet is the default entry (§13a, top of the
 * operational hierarchy). The four operational levels (Fleet/Pad/Well/Section)
 * are wired to Oxbo-P plan-timeline endpoints (§10.9); the three org-upper
 * levels are org-configured + RBAC-gated (§19) and shown locked until the org
 * tree + role scope are wired.
 *
 * Controls: multi-scale zoom (Day…Year) + auto-zoom fit-to-data (§17); a
 * plan/actual filter; a legend. The canvas (CPE spine + tracks) is
 * `PlannerTimeline`. Oxbo-Z owns no numbers — every span is server-computed.
 */
import { useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import type { MessageKey } from "@i18n/messages";
import {
  LEVEL_ORDER,
  WIRED_LEVELS,
  ZOOM_SCALES,
  hasBars,
  type PlannerLevel,
  type ZoomScale,
  type PlanActualMode,
} from "./plannerModel";
import { usePlanTimeline } from "./usePlanTimeline";
import { PlannerTimeline } from "./PlannerTimeline";
import "./planner.css";

const LEVEL_KEY: Record<PlannerLevel, MessageKey> = {
  corporate: "planner.level.corporate",
  regional: "planner.level.regional",
  local: "planner.level.local",
  fleet: "planner.level.fleet",
  pad: "planner.level.pad",
  well: "planner.level.well",
  section: "planner.level.section",
};

const ZOOM_KEY: Record<ZoomScale, MessageKey> = {
  day: "planner.zoom.day",
  week: "planner.zoom.week",
  month: "planner.zoom.month",
  quarter: "planner.zoom.quarter",
  year: "planner.zoom.year",
};

const PA_MODES: { id: PlanActualMode; key: MessageKey }[] = [
  { id: "both", key: "planner.pa.both" },
  { id: "plan", key: "planner.pa.plan" },
  { id: "actual", key: "planner.pa.actual" },
];

const isWired = (level: PlannerLevel) => WIRED_LEVELS.includes(level);

export function Planner() {
  const { t } = useLocale();
  const [level, setLevel] = useState<PlannerLevel>("fleet");
  const [scale, setScale] = useState<ZoomScale>("week");
  const [autoZoom, setAutoZoom] = useState(false);
  const [mode, setMode] = useState<PlanActualMode>("both");

  const { data, wellName, isLoading, isError, refetch } = usePlanTimeline(level);

  // Breadcrumb: the levels down to (and including) the active one.
  const crumbIndex = LEVEL_ORDER.indexOf(level);
  const breadcrumb = LEVEL_ORDER.slice(0, crumbIndex + 1);

  const subtitle =
    level === "well" && wellName
      ? `${t("planner.level.well")} · ${wellName}`
      : t(LEVEL_KEY[level]);

  return (
    <div className="planner">
      <div className="page-head">
        <h1>{t("planner.title")}</h1>
        <p className="subtitle">{t("planner.subtitle")}</p>
      </div>

      {/* Level switcher (§19 hierarchy). Wired levels are active; org-upper
          levels are RBAC/org-config gated and shown locked. */}
      <div className="planner-levels" role="group" aria-label={t("planner.level")}>
        {LEVEL_ORDER.map((lv) => {
          const wired = isWired(lv);
          return (
            <button
              key={lv}
              type="button"
              className={`lvl-btn${lv === level ? " on" : ""}${wired ? "" : " locked"}`}
              aria-pressed={lv === level}
              disabled={!wired}
              title={wired ? undefined : t("planner.levelLocked")}
              onClick={() => wired && setLevel(lv)}
            >
              {t(LEVEL_KEY[lv])}
            </button>
          );
        })}
      </div>

      <nav className="planner-breadcrumb" aria-label="breadcrumb">
        {breadcrumb.map((lv, i) => (
          <span key={lv} className="crumb">
            {i > 0 && <span className="sep" aria-hidden="true">›</span>}
            <span className={lv === level ? "here" : ""}>{t(LEVEL_KEY[lv])}</span>
          </span>
        ))}
      </nav>

      {/* Controls: zoom + auto-zoom + plan/actual filter. */}
      <div className="planner-controls">
        <div className="ctl-grp">
          <span className="ctl-lbl">{t("planner.zoom")}</span>
          <div className="seg" role="group" aria-label={t("planner.zoom")}>
            {ZOOM_SCALES.map((z) => (
              <button
                key={z}
                type="button"
                className={`zoom-btn${scale === z && !autoZoom ? " on" : ""}`}
                aria-pressed={scale === z && !autoZoom}
                disabled={autoZoom}
                onClick={() => setScale(z)}
              >
                {t(ZOOM_KEY[z])}
              </button>
            ))}
          </div>
          <label className="autozoom-cb">
            <input
              type="checkbox"
              checked={autoZoom}
              onChange={(e) => setAutoZoom(e.target.checked)}
            />
            {t("planner.autoZoom")} <span className="dim">({t("planner.autoZoom.hint")})</span>
          </label>
        </div>

        <div className="ctl-grp">
          <span className="ctl-lbl">{t("planner.show")}</span>
          <div className="seg" role="group" aria-label={t("planner.show")}>
            {PA_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`pa-btn${mode === m.id ? " on" : ""}`}
                aria-pressed={mode === m.id}
                onClick={() => setMode(m.id)}
              >
                {t(m.key)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Legend (§13). */}
      <div className="planner-legend" aria-hidden="true">
        <span className="lg lg-plan">{t("planner.legend.plan")}</span>
        <span className="lg lg-actual">{t("planner.legend.actual")}</span>
        <span className="lg lg-offline">{t("planner.legend.offline")}</span>
        <span className="lg lg-npt">{t("planner.legend.npt")}</span>
        <span className="lg lg-today">{t("planner.legend.today")}</span>
        <span className="lg lg-cpe">{t("planner.cpeSpine")}</span>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!hasBars(data)}
        emptyMessage={t("planner.empty")}
        onRetry={refetch}
      >
        {data ? (
          <PlannerTimeline
            resp={data}
            mode={mode}
            scale={scale}
            autoZoom={autoZoom}
            title={t(LEVEL_KEY[level])}
            subtitle={subtitle}
          />
        ) : null}
      </QueryState>
    </div>
  );
}
